/**
 * CampaignRunHistory — the campaign run history that used to live in the
 * Campaign Grid's right-side detail panel.
 *
 * The grid's panel is gone: campaign rows now open the Campaign Launch
 * Workflow (CLW), and this component is the Runs tab of the CLW Sidekick
 * agent sidebar. It renders exactly what the panel's Runs tab rendered:
 *
 *   · every optimize-campaign and edit-campaign message, newest first
 *   · each run collapsed by default; the header row is the toggle
 *   · optimize runs expand to the same result bubble (analysis, insights,
 *     per-suggestion Apply) the panel showed
 *
 * The old tab's "Campaign details →" link is BACK (it was dropped in the move
 * on the assumption CLW was the only surface): it opens the campaign-details
 * variant of /campaign/v3/start (?from=launch), the reconcile view — a
 * different page from the CLW this sidebar lives in.
 *
 * The component is presentational: the parent owns the message fetch, polling
 * and apply dispatch (see useCampaignRunHistory).
 */
import { useEffect, useState } from 'react';
import { summarizeFbReportingData, formatOptimizerCurrency } from '../../../utils/optimizerResultPresentation';

// One optimize-campaign result rendered inside a CLW agent bubble. Mirrors the
// grid panel's OptimizeResultBubble (same json_response.details shapes: Google
// Ads `analysis.actionable_suggestions`, MadHive/Reddit/FB `actionable_items` +
// string `analysis`), condensed for the narrow sidebar.
export function OptimizeResultBubble({ status, details, onApply, isApplied, isApplying }) {
  const fmtVal = (v) => typeof v === 'object' && v !== null
    ? (Array.isArray(v)
        ? v.map(x => (typeof x === 'object' && x !== null ? (x.keyword || JSON.stringify(x)) : String(x))).join(', ')
        : JSON.stringify(v))
    : String(v);

  if (status === 'error' || (details && details.success === false)) {
    const rawError = details?.error;
    const errorText = rawError == null
      ? 'Optimization failed. Check the campaign details page for more information.'
      : (typeof rawError === 'object' ? (rawError.error || rawError.message || JSON.stringify(rawError)) : String(rawError));
    return (
      <div className="clw-ai-msg-bubble" style={{ background: '#FDF2F4', borderColor: '#F8C9D1' }}>
        <strong style={{ color: 'var(--red)' }}>Optimization failed</strong>
        <div style={{ marginTop: 4, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorText}</div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="clw-ai-msg-bubble">
        Optimization finished. Open the campaign details page for the full results.
      </div>
    );
  }

  const analysisData = details.analysis;
  const actionableItems = (typeof analysisData === 'object' && analysisData?.actionable_suggestions)
    || details.actionable_items || [];
  const analysisText = typeof analysisData === 'string'
    ? analysisData
    : analysisData?.campaign_analysis?.overall_performance;
  const insights = (typeof analysisData === 'object' && analysisData?.campaign_analysis?.key_insights) || [];
  // FB optimize carries the raw Meta report it analyzed (details.reporting_data,
  // insights = array of per-ad rows) — surface its campaign totals so the run's
  // stats show right in the panel. Null for platforms without that payload.
  const fbStats = summarizeFbReportingData(details.reporting_data);

  return (
    <div className="clw-ai-msg-bubble">
      {fbStats && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11.5, fontFamily: 'var(--font-mono)', background: 'var(--g1)', borderRadius: 'var(--r-sm)', padding: '6px 8px', marginBottom: 8 }}>
          <span><strong>{formatOptimizerCurrency(fbStats.totals.spend, fbStats.currency)}</strong> spend</span>
          <span><strong>{fbStats.totals.impressions.toLocaleString()}</strong> impr</span>
          <span><strong>{fbStats.totals.clicks.toLocaleString()}</strong> clicks</span>
          <span><strong>{fbStats.totals.ctr.toFixed(2)}%</strong> CTR</span>
          <span><strong>{formatOptimizerCurrency(fbStats.totals.cpc, fbStats.currency)}</strong> CPC</span>
        </div>
      )}
      {analysisText && <div style={{ marginBottom: (insights.length || actionableItems.length) ? 8 : 0 }}>{analysisText}</div>}
      {insights.length > 0 && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
          {insights.map((ins, i) => (
            <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>
              {ins.insight}
              {ins.severity && (
                <span className={`clw-run-pill ${ins.severity === 'HIGH' ? 'clw-run-pill-red' : ins.severity === 'MEDIUM' ? 'clw-run-pill-amber' : 'clw-run-pill-green'}`} style={{ marginLeft: 6 }}>
                  {ins.severity}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!analysisText && actionableItems.length === 0 && (
        <div>Optimization finished — no actionable suggestions returned.</div>
      )}
      {actionableItems.map((s, i) => {
        const hasTarget = (s.target_type && s.target_id) || (s.entity_type && s.entity_name);
        const header = hasTarget
          ? `${(s.action_type || '').replace(/_/g, ' ').toUpperCase()} — ${s.target_type || s.entity_type} ${s.target_id || s.entity_name}`
          : (s.description || (s.action_type || 'Optimization').replace(/_/g, ' ').toUpperCase());
        // An applied suggestion is history, not a choice. It used to look
        // identical to the ones still waiting apart from a small pill at the
        // bottom, so a half-applied run of sixteen read as sixteen open
        // decisions. Greying the whole card sorts done from outstanding at a
        // glance, without hiding what was applied.
        const applied = Boolean(isApplied?.(i));
        // SAME advisory rule as CampaignActivity's optimize rendering — the
        // two surfaces show the same runs and must agree on which
        // suggestions are one-click. Google Ads stamps auto_applicable;
        // DV360's resolver sets advisory; PMax audience-signal is
        // inference-only. Anything advisory renders without Apply.
        const advisory = s.auto_applicable === false
          || s.advisory === true
          || s.action_type === 'audience_signal';
        return (
          <div
            key={i}
            style={{
              border: '1px solid var(--g2)',
              borderRadius: 'var(--r-sm)',
              background: applied ? 'var(--g1)' : 'var(--white)',
              opacity: applied ? 0.65 : 1,
              padding: '8px 10px',
              marginTop: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
              <strong style={{ fontSize: 12 }}>{header}</strong>
              {s.priority && (
                <span className={`clw-run-pill ${s.priority === 'high' ? 'clw-run-pill-red' : s.priority === 'medium' ? 'clw-run-pill-amber' : 'clw-run-pill-gray'}`}>
                  {s.priority}
                </span>
              )}
            </div>
            {hasTarget && s.description && (
              <div style={{ fontSize: 12, marginBottom: 4 }}>{s.description}</div>
            )}
            {s.current_value != null && s.recommended_value != null && (
              <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', marginBottom: 4, wordBreak: 'break-word' }}>
                <span style={{ background: 'var(--g1)', padding: '1px 5px', borderRadius: 3 }}>{fmtVal(s.current_value)}</span>
                {' → '}
                <span style={{ background: '#E8FAF2', color: '#0F7B5F', padding: '1px 5px', borderRadius: 3, fontWeight: 600 }}>{fmtVal(s.recommended_value)}</span>
              </div>
            )}
            {s.suggested_values && Object.keys(s.suggested_values).length > 0 && (
              <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', marginBottom: 4, wordBreak: 'break-word' }}>
                {Object.entries(s.suggested_values).map(([k, v]) => (
                  <div key={k}>{k}: <strong style={{ color: '#0F7B5F' }}>{fmtVal(v)}</strong></div>
                ))}
              </div>
            )}
            {(s.reasoning || s.rationale) && (
              <div style={{ fontSize: 11.5, color: 'var(--g4)' }}>{s.reasoning || s.rationale}</div>
            )}
            {s.expected_impact && (
              <div style={{ fontSize: 11.5, color: 'var(--g4)', marginTop: 2 }}><em>Expected impact:</em> {s.expected_impact}</div>
            )}
            {advisory ? (
              <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--g4)' }}>
                ℹ️ Advisory — review and apply this manually.
              </div>
            ) : onApply && (
              applied
                ? <div style={{ marginTop: 8 }}><span className="clw-run-pill clw-run-pill-green">✓ Applied</span></div>
                : (
                  <button
                    className="clw-btn clw-btn-ai clw-run-apply"
                    style={{ marginTop: 8 }}
                    disabled={isApplying?.(i)}
                    onClick={() => onApply(s, i)}
                  >
                    {isApplying?.(i) ? 'Applying…' : 'Apply changes'}
                  </button>
                )
            )}
          </div>
        );
      })}
    </div>
  );
}

const runPillTone = (status) => (
  status === 'success' ? 'clw-run-pill-green'
    : status === 'error' ? 'clw-run-pill-red'
      : 'clw-run-pill-amber'
);

export function CampaignRunHistory({
  campaignId,
  runs = [],
  loading = false,
  error = null,
  refreshing = false,
  onRefresh = null,
  onApplySuggestion = null,
  isApplied = null,
  applyingKeys = new Set(),
  notice = null, // { ts, ok, text } from the last apply dispatch
}) {
  // Runs start COLLAPSED. A campaign accumulates a run per optimize dispatch,
  // and each expanded body is a full result bubble — with several of them open
  // the tab is a wall of identical failure text and the run history itself
  // (what ran, when, how it ended) scrolls off. The header row carries that
  // history; the body is opt-in.
  const [openRuns, setOpenRuns] = useState(() => new Set());

  useEffect(() => {
    setOpenRuns(new Set());
  }, [campaignId]);

  const toggleRun = (id) => setOpenRuns((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderRun = (r) => (
    <div key={r.id}>
      <button
        type="button"
        className="clw-run-row"
        onClick={() => toggleRun(r.id)}
        aria-expanded={openRuns.has(r.id)}
        title={openRuns.has(r.id) ? 'Collapse this run' : 'Expand this run'}
      >
        <span className="clw-run-caret">{openRuns.has(r.id) ? '▾' : '▸'}</span>
        <strong>{r.agent_type}</strong>
        <small>{new Date(r.created_at).toLocaleString()}</small>
        <span className={`clw-run-pill ${runPillTone(r.status)}`}>{r.status}</span>
      </button>
      {/* The optimizer's raw output, with an Apply button per suggestion. This
          used to sit in the Agents tab of the old grid panel; the agent took
          that tab over, and it belongs with the run that produced it anyway.
          Nothing was lost in the move — the per-suggestion Apply is the same
          call it always was. */}
      {openRuns.has(r.id) && r.agent_type === 'optimize-campaign' && r.status !== 'running' && (
        <div className="clw-run-body">
          <OptimizeResultBubble
            status={r.status}
            details={r.result?.details}
            onApply={onApplySuggestion ? (sg, i) => onApplySuggestion(r, sg, i) : null}
            isApplied={(i) => Boolean(isApplied?.(r.id, i))}
            isApplying={(i) => applyingKeys.has(`${r.id}-${i}`)}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="clw-runs">
      <div className="clw-runs-toolbar">
        <strong>Run history</strong>
        {campaignId && (
          <a
            className="clw-run-details-link"
            href={`/campaigns/${encodeURIComponent(campaignId)}`}
            title="Open the campaign details page"
          >
            Campaign details →
          </a>
        )}
        {onRefresh && (
          <button
            type="button"
            className="clw-run-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            title="Re-fetch this campaign's run history"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>
      {notice && (
        <div className={`clw-run-note ${notice.ok ? 'ok' : 'warn'}`}>
          <span>{notice.text}</span>
        </div>
      )}
      {loading && <div className="clw-runs-loading">Loading runs…</div>}
      {!loading && error && <div className="clw-runs-error">{error}</div>}
      {!loading && !error && runs.length === 0 && (
        <div className="clw-runs-empty">No runs yet for this campaign.</div>
      )}
      {!loading && runs.map(renderRun)}
    </div>
  );
}

export default CampaignRunHistory;
